import { ENDPOINTS } from '@/constants/api'
import { QUERY_KEYS } from '@/constants/query-keys'
import type { QueryOf, RequestBodyOf, ResponseData } from '@/types/api'
import { apiSlice } from './base'

export type Partner = ResponseData<'/api/v1/partners/{id}', 'get'>
export type CustomersData = ResponseData<'/api/v1/partners/{id}/customers', 'get'>
export type Customer = CustomersData['items'][number]
type PartnersListData = ResponseData<'/api/v1/partners/', 'get'>
type PartnersQuery = QueryOf<'/api/v1/partners/', 'get'>
type CustomersQuery = QueryOf<'/api/v1/partners/{id}/customers', 'get'>
type CreatePartnerBody = RequestBodyOf<'/api/v1/partners/', 'post'>
type UpdatePartnerBody = RequestBodyOf<'/api/v1/partners/{id}', 'patch'>
type CreateCustomerBody = RequestBodyOf<'/api/v1/partners/{id}/customers', 'post'>

export const partnersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPartners: builder.query<PartnersListData, PartnersQuery | void>({
      query: (params) => ({ url: ENDPOINTS.PARTNERS.ROOT, ...(params ? { params } : {}) }),
      transformResponse: (response: { data: PartnersListData }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((p) => ({ type: QUERY_KEYS.PARTNER, id: p.id }) as const),
              { type: QUERY_KEYS.PARTNER, id: 'LIST' } as const,
            ]
          : [{ type: QUERY_KEYS.PARTNER, id: 'LIST' } as const],
    }),
    getPartnerById: builder.query<Partner, string>({
      query: (id) => ENDPOINTS.PARTNERS.BY_ID(id),
      transformResponse: (response: { data: Partner }) => response.data,
      providesTags: (_r, _e, id) => [{ type: QUERY_KEYS.PARTNER, id }],
    }),
    createPartner: builder.mutation<Partner, CreatePartnerBody>({
      query: (body) => ({ url: ENDPOINTS.PARTNERS.ROOT, method: 'POST', body }),
      transformResponse: (response: { data: Partner }) => response.data,
      invalidatesTags: [{ type: QUERY_KEYS.PARTNER, id: 'LIST' }],
    }),
    updatePartner: builder.mutation<Partner, { id: string; body: UpdatePartnerBody }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.PARTNERS.BY_ID(id), method: 'PATCH', body }),
      transformResponse: (response: { data: Partner }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: QUERY_KEYS.PARTNER, id },
        { type: QUERY_KEYS.PARTNER, id: 'LIST' },
      ],
    }),
    getCustomers: builder.query<CustomersData, { id: string; query?: CustomersQuery }>({
      query: ({ id, query }) => ({
        url: ENDPOINTS.PARTNERS.CUSTOMERS(id),
        ...(query ? { params: query } : {}),
      }),
      transformResponse: (response: { data: CustomersData }) => response.data,
      providesTags: (result, _e, { id }) =>
        result
          ? [
              ...result.items.map((c) => ({ type: QUERY_KEYS.CUSTOMER, id: c.id }) as const),
              { type: QUERY_KEYS.CUSTOMER, id: `LIST:${id}` } as const,
            ]
          : [{ type: QUERY_KEYS.CUSTOMER, id: `LIST:${id}` } as const],
    }),
    createCustomer: builder.mutation<Customer, { id: string; body: CreateCustomerBody }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.PARTNERS.CUSTOMERS(id), method: 'POST', body }),
      transformResponse: (response: { data: Customer }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: QUERY_KEYS.CUSTOMER, id: `LIST:${id}` }],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetPartnersQuery,
  useGetPartnerByIdQuery,
  useCreatePartnerMutation,
  useUpdatePartnerMutation,
  useGetCustomersQuery,
  useCreateCustomerMutation,
} = partnersApi
